import { getFunctionKeyHeaders } from '../axios/functionKey';

export function assistantConfig() {
  return {
    enabled: process.env.REACT_APP_ASSISTANT_ENABLED !== 'false',
    key: process.env.REACT_APP_ASSISTANT_KEY || '',
    scope: process.env.REACT_APP_ASSISTANT_SCOPE || '',
  };
}

const toolLabels = {
  list_portal_resources: 'Checking available data',
  describe_portal_resource: 'Inspecting available fields',
  query_portal_resource: 'Querying portal data',
  find_job: 'Finding the job',
  get_job_detail: 'Loading job details',
};

export async function askAssistant({ url, config, getToken, message, history, signal, onDelta, onActivity,
  fetchImpl = fetch }) {
  if (!config.enabled) throw new Error('AI chat is not configured yet. Field search is still available.');
  const headers = {
    ...(!config.key ? getFunctionKeyHeaders(url) : {}),
    'Content-Type': 'application/json', Accept: 'text/event-stream, application/json',
  };
  if (config.key) headers['x-functions-key'] = config.key;
  if (config.scope) {
    const token = await getToken(config.scope);
    if (!token) throw new Error('Sign in again to use the assistant.');
    headers.Authorization = `Bearer ${token}`;
  }
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
  const response = await fetchImpl(url, {
    method: 'POST', headers, signal, credentials: 'omit', redirect: 'error', cache: 'no-store',
    body: JSON.stringify({ message, messages: history.filter(m =>
      ['user', 'assistant'].includes(m.role) && typeof m.content === 'string').slice(-30)
      .map(({ role, content }) => ({ role, content })) }),
  });
  if (response.status === 401 || response.status === 403) {
    throw new Error('Assistant access denied. Check API authentication and your Admin access.');
  }
  if (!response.ok) throw new Error(`Assistant request failed (${response.status}). Please try again later.`);
  if (response.headers.get('content-type')?.includes('application/json')) {
    const payload = await response.json();
    if (typeof payload.message !== 'string') throw new Error('Unexpected assistant response.');
    onDelta(payload.message);
    return;
  }
  if (!response.headers.get('content-type')?.includes('text/event-stream') || !response.body) {
    throw new Error('Unexpected assistant response format.');
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let completed = false;
  const consume = block => {
    const data = block.split(/\r?\n/).filter(line => line.startsWith('data:'))
      .map(line => line.slice(5).replace(/^ /, '')).join('\n');
    if (!data) return;
    if (data === '[DONE]') { completed = true; return; }
    let event;
    try { event = JSON.parse(data); } catch { throw new Error('Invalid assistant stream. Please try again.'); }
    if (event.type === 'error') throw new Error(event.message || 'The assistant could not complete this answer.');
    if (event.type === 'delta' && typeof event.text === 'string') { onActivity(null); onDelta(event.text); }
    if (event.type === 'tool_call') onActivity(toolLabels[event.name] || 'Checking portal data');
    if (event.type === 'tool_result') onActivity(null);
  };
  try {
    while (!completed) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });
      let match;
      while (!completed && (match = /\r?\n\r?\n/.exec(buffer))) {
        consume(buffer.slice(0, match.index));
        buffer = buffer.slice(match.index + match[0].length);
      }
      if (done) {
        if (!completed && buffer.trim()) consume(buffer);
        if (!completed) throw new Error('The connection ended before the answer completed. Please try again.');
      }
    }
  } finally {
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}
